import { Dispatch, useEffect, useState, useMemo } from 'react';
import track from 'c9-tracking';

import { GiftCardKeys, IAction, States, WhitelabelPartner } from '../interfaces/interfaces';

import { genericRequest } from '../store/Actions';
import { SET_DATA_MODEL_VALUE, SET_LABELS } from '../store/ActionKeys';

import { IConfigProps, useConfigContext } from '../context/ConfigContext';
import { GiftCardContext } from '../context/GiftCardContext';
import { useLoggerContext } from '../context/LoggerContext';
import { useStateValue } from '../hooks/UseStateValue';

import { TProvider } from '../interfaces/interfaces';

type TGetGiftcard = (props: {
    giftcardEndpoint: IConfigProps['endpoint']['giftcard'];
    product: IConfigProps['product'];
    partner: States['selection']['partner'];
    logError: (errorMessage: string) => void;
    dispatch: Dispatch<IAction>;
}) => Promise<void>;

type TUseFetchGiftcard = (
    partner: WhitelabelPartner | undefined,
    config: IConfigProps,
    logError: (errorMessage: string) => void,
    dispatch: Dispatch<IAction>
) => void;

/* ========================================= Local Functions ========================================= */
const getGiftcard: TGetGiftcard = async ({ giftcardEndpoint, product, partner, dispatch, logError }) => {
    try {
        let giftCardKeys = GiftCardKeys[product];
        if (partner === WhitelabelPartner.BABY_SAM) giftCardKeys = GiftCardKeys.CHID_TB;

        const response = await genericRequest({
            ...giftcardEndpoint,
            url: `${giftcardEndpoint.url}?key=${giftCardKeys}`
        });

        dispatch({
            type: SET_DATA_MODEL_VALUE,
            payload: {
                productKey: product,
                dataKey: 'giftcard',
                value: response
            }
        });
        track.info([{ key: 'gift card status: ', value: response?.status }]);

        const isGiftcardActive = response.status === 'active';

        // get labels if giftcard is active
        if (isGiftcardActive) {
            const labelResponse = await genericRequest({
                method: 'GET',
                url: 'https://www.topdanmark.dk/rest/resourcebundle/giftcard/',
                headers: {}
            });

            dispatch({
                type: SET_LABELS,
                payload: {
                    productKey: product,
                    labelKey: 'giftcard',
                    value: labelResponse
                }
            });
        }
    } catch (error: any) {
        logError(error.message);
    }
};

const useFetchGiftcard: TUseFetchGiftcard = (partner, config, logError, dispatch) => {
    useEffect(() => {
        if (partner !== undefined) {
            getGiftcard({
                giftcardEndpoint: config.endpoint.giftcard,
                product: config.product,
                partner,
                logError,
                dispatch
            });
        }
    }, [partner, config, logError, dispatch]);
};

/* ========================================= Main Component ========================================= */
const GiftCardProvider = ({ children }: TProvider) => {
    const [{ model, labels }] = useStateValue();

    const [
        {
            selection: { partner }
        },
        dispatch
    ] = useStateValue();
    const config = useConfigContext();
    const { logError } = useLoggerContext();

    const [showGiftCard, setShowGiftCard] = useState<boolean>(false);
    const [data, setData] = useState<any>({});
    const [strings, setStrings] = useState<any>({});

    useFetchGiftcard(partner, config, logError, dispatch);

    useEffect(() => {
        if (labels[config.product]?.giftcard) {
            setStrings(labels[config.product].giftcard);
        }

        if (model[config.product]?.giftcard) {
            setData(model[config.product].giftcard);
        }
    }, [model, config.product, labels]);

    useEffect(() => {
        if (!!Object.keys(data).length && !!Object.keys(strings).length && data.status === 'active') {
            setShowGiftCard(true);
        }
    }, [data, strings]);

    const items = useMemo(
        () => ({
            showGiftCard,
            data,
            strings
        }),
        [showGiftCard, data, strings]
    );

    return <GiftCardContext.Provider value={items}>{children}</GiftCardContext.Provider>;
};

export default GiftCardProvider;
